import type { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import type {
  TransferQueueAppointment,
  TransferQueueConnection,
  TransferQueueSortField,
  TransferQueueStatusTransition,
} from '#services/api/transfer-queue.ts';

import * as React from 'react';
import { Dialog } from '@mend/mui';
import { Box, Button, Card, Chip, Link, Stack, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router';

import { ExportToolbar } from '#components/Drilldown/Toolbar.tsx';
import { config } from '#config.ts';
import { transferQueueAppointments } from '#lib/react-query/queries.ts';
import { convertUtcStringToLocalDate, formatDurationFromSeconds } from '#utils/dates.ts';
import { useTransferQueue } from './use-transfer-queue.ts';

const pageSizeOptions = [10, 25, 50, 100] as const;
const defaultPaginationModel = { page: 0, pageSize: 25 } as const;
const defaultSortModel = [{ field: 'enteredQueueAt', sort: 'desc' }] as const;

function formatTimestamp(timestamp: string | null) {
  return timestamp ? format(convertUtcStringToLocalDate(timestamp), 'MM/dd/yyyy | h:mmaaa') : '--';
}

function getPersonName(firstName: string | null, lastName: string | null) {
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || '--';
}

const columns: GridColDef<TransferQueueAppointment>[] = [
  {
    field: 'appointmentId',
    headerName: 'Appointment ID',
    width: 135,
    renderCell: (params) => (
      <Link component={RouterLink} to={`/?tab=transferQueue&qId=${params.row.appointmentId}`}>
        {params.row.appointmentId}
      </Link>
    ),
  },
  {
    field: 'history',
    headerName: 'History',
    width: 125,
    sortable: false,
    disableExport: true,
    renderCell: (params) => <EpisodeHistoryButton appointment={params.row} />,
  },
  { field: 'episodeNumber', headerName: 'Episode', width: 90, sortable: false },
  {
    field: 'patient',
    headerName: 'Patient',
    width: 180,
    sortable: false,
    valueGetter: (_value, row) => getPersonName(row.patientFirstName, row.patientLastName),
    renderCell: (params) =>
      params.row.patientId ? (
        <Link
          href={new URL(`/patient/${params.row.patientId}`, config.PORTAL_BASE_URL).toString()}
          target="_blank"
          rel="noreferrer"
        >
          {getPersonName(params.row.patientFirstName, params.row.patientLastName)}
        </Link>
      ) : (
        getPersonName(params.row.patientFirstName, params.row.patientLastName)
      ),
  },
  {
    field: 'patientChartId',
    headerName: 'Chart ID',
    width: 115,
    sortable: false,
    valueFormatter: (value: string | null) => value ?? '--',
  },
  {
    field: 'provider',
    headerName: 'Scheduled Provider',
    width: 190,
    sortable: false,
    valueGetter: (_value, row) => getPersonName(row.providerFirstName, row.providerLastName),
    renderCell: (params) =>
      params.row.providerId ? (
        <Link
          href={new URL(`/provider/${params.row.providerId}`, config.PORTAL_BASE_URL).toString()}
          target="_blank"
          rel="noreferrer"
        >
          {getPersonName(params.row.providerFirstName, params.row.providerLastName)}
        </Link>
      ) : (
        getPersonName(params.row.providerFirstName, params.row.providerLastName)
      ),
  },
  {
    field: 'appointmentTypeName',
    headerName: 'Appointment Type',
    width: 170,
    sortable: false,
    valueFormatter: (value: string | null | undefined) => value ?? '--',
  },
  {
    field: 'addressName',
    headerName: 'Location',
    width: 150,
    sortable: false,
    valueFormatter: (value: string | null | undefined) => value ?? '--',
  },
  {
    field: 'entryStatusName',
    headerName: 'Entry Status',
    width: 160,
    sortable: false,
  },
  {
    field: 'entryAssignedUserName',
    headerName: 'Assigned at Entry',
    width: 175,
    sortable: false,
    valueFormatter: (value: string | null) => value ?? '--',
  },
  {
    field: 'enteredQueueAt',
    headerName: 'Entered Queue',
    width: 180,
    valueFormatter: (value: string) => formatTimestamp(value),
  },
  {
    field: 'exitedQueueAt',
    headerName: 'Exited Queue',
    width: 180,
    valueFormatter: (value: string | null) => formatTimestamp(value),
  },
  {
    field: 'secondsInQueue',
    headerName: 'Time in Queue',
    width: 130,
    valueFormatter: (value: number) => formatDurationFromSeconds(value, 'short'),
  },
  {
    field: 'isCurrentlyQueued',
    headerName: 'Queue State',
    width: 125,
    sortable: false,
    valueFormatter: (value: 0 | 1) => (value ? 'Current' : 'Completed'),
    renderCell: (params) => (
      <Chip
        label={params.row.isCurrentlyQueued ? 'Current' : 'Completed'}
        color={params.row.isCurrentlyQueued ? 'warning' : 'success'}
        size="small"
      />
    ),
  },
  {
    field: 'connectionCount',
    headerName: 'Connections',
    width: 115,
    sortable: false,
  },
  {
    field: 'patientDisconnected',
    headerName: 'Patient Disconnected',
    width: 175,
    sortable: false,
    valueFormatter: (value: 0 | 1) => (value ? 'Yes' : 'No'),
  },
];

export function TransferQueueAppointments() {
  const transferQueue = useTransferQueue();
  const [paginationModel, setPaginationModel] =
    React.useState<GridPaginationModel>(defaultPaginationModel);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([...defaultSortModel]);
  const activeSort = sortModel[0];
  const query = useQuery(
    transferQueueAppointments({
      ...transferQueue.getRequestParams(),
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      sortBy: (activeSort?.field ?? 'enteredQueueAt') as TransferQueueSortField,
      sortDirection: activeSort?.sort === 'asc' ? 'ASC' : 'DESC',
    })
  );

  function handleSortModelChange(nextSortModel: GridSortModel) {
    setSortModel(nextSortModel.length > 0 ? nextSortModel : [...defaultSortModel]);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }

  return (
    <Card sx={{ p: 2 }}>
      <Typography component="h2" variant="h6" mb={2}>
        Queue Appointments
      </Typography>

      <DataGridPro
        rows={query.data?.data ?? []}
        columns={columns}
        loading={query.isLoading}
        showToolbar
        slots={{ toolbar: ExportToolbar }}
        getRowId={(row) => `${row.appointmentId}-${row.episodeNumber}`}
        rowCount={query.data?.totalItems ?? 0}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={pageSizeOptions}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        checkboxSelection
        disableRowSelectionOnClick
        sx={{ minHeight: 520 }}
      />
    </Card>
  );
}

function EpisodeHistoryButton(props: { appointment: TransferQueueAppointment }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const titleId = React.useId();
  const { statusTransitions, connections } = props.appointment;
  const historyEvents = [
    ...statusTransitions.map((transition) => ({
      id: `status-${transition.occurredAt}-${transition.priorStatusId ?? 'none'}-${transition.newStatusId ?? 'none'}-${transition.changedByUserId ?? 'none'}`,
      occurredAt: transition.occurredAt,
      event: { type: 'status' as const, transition },
    })),
    ...connections.map((connection) => ({
      id: `connection-${connection.id}`,
      occurredAt: connection.connectedAt,
      event: { type: 'connection' as const, connection },
    })),
  ].sort(
    (left, right) =>
      convertUtcStringToLocalDate(left.occurredAt).getTime() -
      convertUtcStringToLocalDate(right.occurredAt).getTime()
  );

  return (
    <>
      <Button size="small" variant="text" onClick={() => setIsOpen(true)}>
        View History
      </Button>

      {isOpen && (
        <Dialog.Root
          open
          onClose={() => setIsOpen(false)}
          fullWidth
          maxWidth="md"
          aria-labelledby={titleId}
        >
          <Dialog.Header>
            <Dialog.Title id={titleId}>Queue Episode History</Dialog.Title>
            <Dialog.Description>
              Appointment {props.appointment.appointmentId}, episode{' '}
              {props.appointment.episodeNumber}
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Content>
            <Timeline title="History">
              {historyEvents.length > 0 ? (
                historyEvents.map((historyEvent) =>
                  historyEvent.event.type === 'status' ? (
                    <StatusTransitionItem
                      key={historyEvent.id}
                      transition={historyEvent.event.transition}
                    />
                  ) : (
                    <ConnectionItem
                      key={historyEvent.id}
                      connection={historyEvent.event.connection}
                    />
                  )
                )
              ) : (
                <Typography color="text.secondary">No episode history recorded.</Typography>
              )}
            </Timeline>
          </Dialog.Content>

          <Dialog.Footer>
            <Dialog.DismissButton />
          </Dialog.Footer>
        </Dialog.Root>
      )}
    </>
  );
}

function Timeline(props: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Typography component="h3" variant="subtitle1" mb={1.5}>
        {props.title}
      </Typography>
      <Stack direction="column" spacing={1.5} sx={{ width: '100%' }}>
        {props.children}
      </Stack>
    </Box>
  );
}

function TimelineItem(props: { title: string; timestamp: string; children: React.ReactNode }) {
  return (
    <Box sx={{ width: '100%', borderLeft: 2, borderColor: 'divider', pl: 2, py: 0.5 }}>
      <Typography variant="subtitle2">{props.title}</Typography>
      <Typography variant="caption" color="text.secondary">
        {formatTimestamp(props.timestamp)}
      </Typography>
      <Box mt={0.5}>{props.children}</Box>
    </Box>
  );
}

function StatusTransitionItem(props: { transition: TransferQueueStatusTransition }) {
  const { transition } = props;

  return (
    <TimelineItem title={getStatusTransitionTitle(transition)} timestamp={transition.occurredAt}>
      <Stack direction="column" spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          {getAssigneeTransitionDescription(transition)}
        </Typography>
        {transition.changedByUserName ? (
          <Typography variant="body2" color="text.secondary">
            Changed by {transition.changedByUserName}.
          </Typography>
        ) : null}
      </Stack>
    </TimelineItem>
  );
}

function getStatusTransitionTitle(transition: TransferQueueStatusTransition) {
  const priorStatus = transition.priorStatusName ?? 'unknown';
  const newStatus = transition.newStatusName ?? 'unknown';

  if (transition.priorStatusId === null && transition.newStatusId !== null) {
    return `Appointment entered the transfer queue with ${newStatus} status`;
  }

  if (transition.priorStatusId !== null && transition.newStatusId === null) {
    return `Appointment exited the transfer queue from ${priorStatus} status`;
  }

  if (transition.priorStatusId === null && transition.newStatusId === null) {
    return 'No queue status transition happened';
  }

  if (transition.priorStatusId === transition.newStatusId) {
    return `Appointment remained in ${newStatus} status`;
  }

  return `Appointment status changed from ${priorStatus} to ${newStatus}`;
}

function getAssigneeTransitionDescription(transition: TransferQueueStatusTransition) {
  const priorAssigneeId = transition.priorAssignedUserId;
  const newAssigneeId = transition.newAssignedUserId;

  if (priorAssigneeId === newAssigneeId) {
    return 'No assignee transition happened.';
  }

  if (priorAssigneeId === null) {
    return `Appointment was assigned to user ID ${newAssigneeId}.`;
  }

  if (newAssigneeId === null) {
    return `Assignee with user ID ${priorAssigneeId} was removed.`;
  }

  return `Assignee changed from user ID ${priorAssigneeId} to user ID ${newAssigneeId}.`;
}

function ConnectionItem(props: { connection: TransferQueueConnection }) {
  const { connection } = props;
  const flags = [
    connection.connectedAtQueueEntry ? 'connected at queue entry' : null,
    connection.connectedWhileQueued ? 'connected while queued' : null,
    connection.disconnectedWhileQueued ? 'disconnected while queued' : null,
  ].filter(Boolean);

  return (
    <TimelineItem
      title={`${connection.displayName ?? 'Unknown participant'} connected as ${connection.participantType}`}
      timestamp={connection.connectedAt}
    >
      <Stack direction="column" spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          Queue overlap: {formatDurationFromSeconds(connection.secondsConnectedWhileQueued)}.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {connection.disconnectedAt
            ? `Disconnected at ${formatTimestamp(connection.disconnectedAt)}.`
            : 'Connection remained active.'}
        </Typography>
        {flags.length > 0 ? (
          <Typography variant="body2" color="text.secondary">
            {flags.join(', ')}.
          </Typography>
        ) : null}
      </Stack>
    </TimelineItem>
  );
}
